import { useState, FormEvent } from "react";

interface MaintenanceGatePageProps {
    onAccessGranted: () => void;
    onVerify: (code: string) => Promise<void>;
    error?: string;
}

/**
 * Full-page gate for admin panel when maintenance mode is enabled.
 * Uses a simpler design that fits the admin aesthetic.
 */
export const MaintenanceGatePage = ({
    onAccessGranted,
    onVerify,
    error: externalError
}: MaintenanceGatePageProps) => {
    const [code, setCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(externalError || null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!code.trim()) {
            setError("Please enter an access code");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await onVerify(code);
            onAccessGranted();
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || "Invalid access code");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
            {/* Background gradient */}
            <div
                className="fixed inset-0 pointer-events-none"
                style={{
                    background: 'radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
                }}
            />

            <div className="w-full max-w-md relative z-10">
                <div className="bg-slate-800 rounded-2xl shadow-2xl p-8 border border-slate-700">
                    {/* Lock Icon */}
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <span className="w-8 h-8 text-white" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_3c1e8fb9.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3c1e8fb9.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl font-semibold text-white text-center mb-2">
                        Admin Access Required
                    </h1>
                    <p className="text-slate-400 text-center mb-8">
                        This environment is restricted. Enter your access code to continue.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="access-code"
                                className="block text-sm font-medium text-slate-300 mb-2"
                            >
                                Access Code
                            </label>
                            <input
                                id="access-code"
                                type="password"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                placeholder="Enter your access code"
                                className={`
                  w-full px-4 py-3 rounded-xl border bg-slate-900 
                  text-white placeholder:text-slate-500
                  focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500
                  transition-all duration-200
                  ${error ? 'border-red-500 bg-red-900/20' : 'border-slate-600'}
                `}
                                autoComplete="off"
                                autoFocus
                                disabled={isLoading}
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 rounded-lg px-4 py-3 border border-red-500/30">
                                <span className="w-5 h-5 flex-shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_cb547b3b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_cb547b3b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`
                w-full py-3 px-4 rounded-xl font-medium text-white
                bg-gradient-to-r from-indigo-500 to-purple-600
                hover:from-indigo-600 hover:to-purple-700
                focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 focus:ring-offset-slate-800
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-200
              `}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span className="animate-spin w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_a9e886ed.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a9e886ed.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    Verifying...
                                </span>
                            ) : (
                                "Access Admin Panel"
                            )}
                        </button>
                    </form>

                    {/* Info */}
                    <p className="text-xs text-slate-500 text-center mt-6">
                        Contact your administrator if you don't have an access code.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MaintenanceGatePage;
